
import { AlertTriangle, History, RotateCcw } from "lucide-react";
import type { RuleResponse } from "../../gen/models";
import { DiffViewer } from "../DiffViewer";

interface RuleHistoryLineageProps {
  history: RuleResponse[];
  currentRule: RuleResponse | null;
  selectedDiffRev: RuleResponse | null;
  onSelectDiffRev: (rev: RuleResponse) => void;
  onRequestRevert?: (rev: RuleResponse) => void;
}

export function RuleHistoryLineage({
  history,
  currentRule,
  selectedDiffRev,
  onSelectDiffRev,
  onRequestRevert,
}: RuleHistoryLineageProps) {
  return (
    <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-5 shadow-2xs space-y-4">
      <div className="border-b border-stone-100 dark:border-stone-800 pb-2.5 flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800 dark:text-stone-200 flex items-center gap-1.5">
          <History className="w-4 h-4 text-emerald-600" />
          <span>Revision Lineage</span>
        </h3>
        <span className="text-[11px] text-stone-400 dark:text-stone-500 font-semibold">
          {history.length} Version{history.length === 1 ? "" : "s"}
        </span>
      </div>

      {history.length === 0 ? (
        <div className="py-8 text-center text-xs text-stone-400 dark:text-stone-500">
          No previous revisions. This rule is at root v1.
        </div>
      ) : (
        <div className="space-y-3 relative pl-4 before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-stone-200 dark:before:bg-stone-800">
          {history.map((rev) => {
            const isCurrent = rev.id === currentRule?.id;
            const isSelectedForDiff = selectedDiffRev?.id === rev.id;
            const isActive = rev.status === "active";
            const isTooLong = Boolean(rev.instruction && rev.instruction.length > 1000);
            const charCount = rev.instruction ? rev.instruction.length : 0;

            return (
              <div key={rev.id} className="relative group">
                {/* Timeline Dot */}
                <div
                  className={`absolute -left-4 top-2 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-stone-900 ${
                    isCurrent
                      ? isTooLong
                        ? "bg-amber-500 ring-2 ring-amber-500/20"
                        : "bg-emerald-600 ring-2 ring-emerald-500/20"
                      : isTooLong
                      ? "bg-amber-500/80"
                      : "bg-stone-400 dark:bg-stone-600"
                  }`}
                />

                <div
                  onClick={() => onSelectDiffRev(rev)}
                  className={`p-3 rounded-lg border text-xs cursor-pointer transition select-none ${
                    isCurrent
                      ? `${
                          isTooLong
                            ? "bg-amber-50/30 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 ring-1 ring-amber-400/20"
                            : "bg-emerald-50/50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800"
                        } text-stone-900 dark:text-stone-100`
                      : isSelectedForDiff
                      ? `${
                          isTooLong
                            ? "bg-stone-100 dark:bg-stone-800 border-amber-400 dark:border-amber-600 ring-1 ring-amber-400/20"
                            : "bg-stone-100 dark:bg-stone-800 border-stone-300 dark:border-stone-700"
                        } text-stone-900 dark:text-stone-100`
                      : isTooLong
                      ? "bg-amber-50/20 dark:bg-amber-950/20 border-amber-300/80 dark:border-amber-700/80 hover:bg-amber-50/40 dark:hover:bg-amber-900/30 text-stone-700 dark:text-stone-200"
                      : "bg-stone-50/40 dark:bg-stone-800/40 border-stone-200 dark:border-stone-700/80 hover:bg-stone-50 dark:hover:bg-stone-800 text-stone-600 dark:text-stone-300"
                  }`}
                >
                  <div className="flex items-center justify-between font-medium gap-1.5">
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      <span
                        className={`font-mono font-bold ${
                          !isActive ? "line-through text-stone-400 dark:text-stone-500" : ""
                        }`}
                      >
                        Version {rev.version}
                      </span>
                      {rev.reverted_from_version && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800/80"
                          title={`Reverted from Version ${rev.reverted_from_version}`}
                        >
                          <RotateCcw className="w-2.5 h-2.5" />
                          <span>Revert of v{rev.reverted_from_version}</span>
                        </span>
                      )}
                      {isTooLong && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[9px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-800 shrink-0 shadow-2xs"
                          title={`Directive is ${charCount.toLocaleString()} characters (exceeds recommended 1,000 char threshold)`}
                        >
                          <AlertTriangle className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400 shrink-0" />
                          <span>{charCount.toLocaleString()} chars</span>
                        </span>
                      )}
                    </div>
                    <span
                      className={`px-1.5 py-0.2 rounded text-[10px] uppercase font-semibold border shrink-0 ${
                        rev.status === "active"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800/80"
                          : rev.status === "superseded"
                          ? "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                          : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800/80"
                      }`}
                    >
                      {rev.status}
                    </span>
                  </div>

                  <div
                    className={`text-[11px] truncate pt-1 ${
                      !isActive
                        ? "line-through text-stone-400 dark:text-stone-500 opacity-75"
                        : "text-stone-600 dark:text-stone-300"
                    }`}
                  >
                    {rev.title}
                  </div>

                  <div className="text-[10px] text-stone-400 dark:text-stone-500 pt-2 flex items-center justify-between border-t border-stone-100 dark:border-stone-800/60 mt-2">
                    <div className="flex items-center gap-1.5 truncate">
                      <span>{new Date(rev.created_at).toLocaleDateString()}</span>
                      {(rev.created_by_name || rev.created_by) && (
                        <>
                          <span>•</span>
                          <span className="truncate max-w-[100px]">{rev.created_by_name || rev.created_by}</span>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {isTooLong && (
                        <span className="text-[9px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                          Over Limit
                        </span>
                      )}
                      {onRequestRevert && !isActive && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestRevert(rev);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-300 dark:border-stone-700 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 dark:hover:bg-emerald-950/50 dark:hover:text-emerald-300 dark:hover:border-emerald-800 transition cursor-pointer shadow-2xs shrink-0"
                          title={`Make Version ${rev.version} the current active version`}
                        >
                          <RotateCcw className="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" />
                          <span>Make Current</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface RuleSupersedeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExecute: () => void;
  currentVersion?: number;
  supersedeTitle: string;
  onChangeTitle: (val: string) => void;
  supersedeInstruction: string;
  onChangeInstruction: (val: string) => void;
  oldInstruction?: string;
  saving: boolean;
}

export function RuleSupersedeModal({
  isOpen,
  onClose,
  onExecute,
  currentVersion = 1,
  supersedeTitle,
  onChangeTitle,
  supersedeInstruction,
  onChangeInstruction,
  oldInstruction = "",
  saving,
}: RuleSupersedeModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 dark:bg-black/70 backdrop-blur-xs">
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 shadow-xl max-w-4xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
            Create New Rule Version (v{currentVersion + 1})
          </h3>
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
            Rules are immutable. Creating this revision will supersede the current version (v{currentVersion}) and immediately activate v{currentVersion + 1}.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">Revision Title</label>
            <input
              type="text"
              value={supersedeTitle}
              onChange={(e) => onChangeTitle(e.target.value)}
              className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs text-stone-900 dark:text-stone-100 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">Updated Instruction</label>
            <textarea
              rows={5}
              value={supersedeInstruction}
              onChange={(e) => onChangeInstruction(e.target.value)}
              className="w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 p-2 text-xs font-mono text-stone-900 dark:text-stone-100 focus:border-stone-500 dark:focus:border-stone-400 focus:outline-hidden"
            />
          </div>

          {oldInstruction && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-stone-700 dark:text-stone-300 block">Live Changes Diff</label>
              <div className="max-h-60 overflow-y-auto border border-stone-200 dark:border-stone-800 rounded-lg">
                <DiffViewer
                  oldTitle={`v${currentVersion} Current`}
                  newTitle={`v${currentVersion + 1} New Version`}
                  oldText={oldInstruction}
                  newText={supersedeInstruction}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-xs font-medium text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onExecute}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white disabled:opacity-50"
          >
            Confirm & Activate vNext
          </button>
        </div>
      </div>
    </div>
  );
}
